
import * as Sentry from "@sentry/vue";
import { loadStripe } from "@stripe/stripe-js";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import Modal from "~/components/Modal.vue";
import orderSummary from "~/components/ship/orderSummary.vue";
import Validation from "~/entities/Validation";
import ValidationPayment from "~/entities/ValidationModules/ValidationPayment";

export default Vue.extend({
  name: "ShipPayment",
  components: { orderSummary, Modal },
  data() {
    return {
      stepNumber: 5,
      validation: new Validation(new ValidationPayment()),
      section1Open: false,
      section2Open: false,
      section3Open: false,
      section4Open: true,
      section5Open: true,
      isOpenModalToS: false as boolean,
      isOpenModalSP: false as boolean,

      buckUpList: [
        { id: "buk_1", label: "$1", value: 1 },
        { id: "buk_2", label: "$2", value: 2 },
        { id: "buk_3", label: "$3", value: 3 },
        { id: "buk_4", label: "$4", value: 4 },
        { id: "buk_5", label: "$5", value: 5 },
      ],
      buckUpOpen: true,
      paymentDetails: {
        PO: "" as String,
        buk_up_for_bikes: "" as String,
        selectedPaymentMethodType: "" as String,
        selectedPaymentMethod: undefined as any,
      } as any,

      promoCode: "" as string,
      promoCodeIsValid: false,
      promoCodeValidated: false,
      promoCodeApplied: false,

      customerBalanceToApply: 0 as number,
      customerBalanceAvailable: 0 as number,
      customerBalanceValid: false,
      customerBalanceIsApplied: false,
      subtotalIsCovered: false,

      isSubmitting: false,
      autoBillable: false,
      clientToken: "",
      savePM: true,

      // stripe
      stripeLoading: false,
      stripe: undefined, // stripe handler
      elements: undefined, // stripe element helpers
      paymentElement: undefined, // stripe element
      paymentElementOptions: undefined, // stripe element's options
      paymentElementAppearance: undefined, // stripe element's appearance's options
      paymentMethods: [],
      selectedPaymentMethodType: undefined,
      selectedPaymentMethodIndex: 0,
      selectedPaymentMethod: undefined as any,
      source: undefined, // id of confirmed payment intent, if there is one
      orderCreatedResponse: undefined,
      paypalCapturedOrderId: undefined as any, // PayPal order id kept for post-capture confirmation recovery

      stripeFrame: undefined, // HTML element of the element
      stripeElementMounted: false, // status of the stripe element
      stripeEvent: undefined, // results of the stripe element event triggers
      isStripeValid: false, // status of stripe and all its elements
      using: "element" as string,
      stripeMin: 0.5 as number, // stripe minimum charge amount
      stripeDescription: "Bikeflights.com Order: #{orderID} for ${subTotal}." as string, // stripe charge description
      paypalAmount: '0' as string,
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices",
      alertResponses: "errorsBag/alertResponses",
      boxes: "ship/boxes",
      trips: "ship/trips",
      purpose: "ship/purpose",
      rates: "ship/rates",
      premiumProtection: "ship/premiumProtection",
      selectedRateSpeedIndex: "ship/selectedRateSpeedIndex",
      selectedRateSpeed: "ship/selectedRateSpeed",
      units: "ship/units",
      subTotal: "payment/subTotal",
      buckForBikes: "payment/buckForBikes",
      temporalOrderIds: "ship/temporalOrderIds",
      session: "user/session",
      PO: "payment/PO",
      fees: "ship/fees",
      promoDiscount: "payment/promoDiscount",
      discount: "payment/discount",
      discountTotal: "payment/discountTotal",
      customerBalance: "payment/customerBalance",
      customerBalanceApplied: "payment/customerBalanceApplied",
      currentEvent: "eventShipping/currentEvent",

      promoDiscountAmount: "payment/promoDiscountAmount",
    }),
    purchaseOrderNumber: {
      get():number {
        return this.PO as number;
      },
      set(newVal:number):number {
        this.paymentDetails.PO = newVal;
        this.setPO(newVal);
        return newVal;
      },
    },
  },
  watch: {
    loading: {
      deep: true,
      immediate: true,
      handler(newVal, oldVal) {
        const _this = this as any;
        const bufferTime = 750;
        if (!newVal && oldVal && _this.validation.module) {
          // if loading ended, and the validation is mounted
          setTimeout(() => {
            // check element mounted
            if (!_this.stripeElementMounted) {
              // if after bufferTime stripeMounted is false, BIG ERROR.
              (this as any).validation.module.requestAlert({
                event: { selector: "stripeBlocked" },
                type: "payment",
              });
            }
            // check frame
            else if (_this.stripeFrame && !_this.stripeFrame.loaded) {
              // if stripe completed mounting then stripeFrame wont be undefined, still, needs to check that the frame is truly loaded
              (this as any).validation.module.requestAlert({
                event: { selector: "stripeBlocked" },
                type: "payment",
              });
            }
          }, bufferTime); // buffer times
        }
      },
    },
  },
  async created() {
    // ask for autoBillable?
    const _this = this as any;

    _this.paymentElementOptions = {
      layout: {
        type: "accordion",
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: false,
      },
      business: { name: "Bikeflights" },
      paymentMethodOrder: ["card", "cashapp", "klarna"],
    };
    // set theme to default
    _this.paymentElementAppearance = {
      theme: "stripe",
      variables: {
        colorPrimary: "#00B5E2",
      },
    };

    await _this.fetchPaymentMethods();
    if (_this.paymentMethods && _this.paymentMethods.length) {
      _this.using = "methods";
    } else {
      _this.using = "element";
    }
    _this.setLoading(false);

    this.mountPaypalConfig();
    if (_this.using === "element") {
      _this.setStripeLoading(true);
      _this.stripeElementMounted = await _this.mountStripeElement(); // stripeElementMounted would be replaced or added to a response from an event listener to the 'ready' event
      _this.setStripeLoading(false);
    }
  },
  mounted() {
    // ask for autoBillable?
    window.scrollTo(0, 0);
    const _this = this as any;
    try {
      if (_this.buckForBikes) {
        _this.paymentDetails.buk_up_for_bikes = _this.buckUpList.filter(
          (a: any) => a.value === _this.buckForBikes
        )[0];
      }
      if (_this.PO) {
        _this.paymentDetails.PO = _this.PO;
      }
      if (_this.customerBalance && _this.customerBalance > 0) {
        _this.customerBalanceAvailable = _this.customerBalance;
        if (_this.customerBalanceAvailable > _this.subTotal) {
          _this.customerBalanceToApply = _this.subTotal;
        } else {
          _this.customerBalanceToApply = _this.customerBalance;
        }
        _this.adjustCustomerBalanceToApply();
        _this.validateCustomerBalance();
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    try {
      if (_this.customerBalanceApplied && _this.customerBalanceApplied > 0) {
        _this.section3Open = true;
        _this.customerBalanceIsApplied = true;
        _this.customerBalanceToApply = _this.customerBalanceApplied;
        _this.customerBalanceValid =
          _this.customerBalanceToApply <= _this.customerBalanceAvailable;
      }
    } catch (e) {
      _this.customerBalanceValid = false;
      Sentry.captureException(e);
    }

    try {
      if (
        _this.promoDiscount &&
        _this.promoDiscount.name &&
        _this.promoDiscount.discount
      ) {
        _this.section2Open = true;
        _this.promoCode = _this.promoDiscount.name;
        _this.promoCodeApplied = true;
        _this.promoCodeIsValid = true;
      }
    } catch (e) {
      _this.promoCodeIsValid = false;
      Sentry.captureException(e);
    }

    try {
      // check if total is 0 or less than stripe minimum
      if (
        _this.discount ||
        _this.customerBalanceIsApplied ||
        _this.promoCodeApplied
      ) {
        _this.subtotalIsCovered =
          (_this.customerBalanceIsApplied &&
            _this.subTotal < _this.stripeMin) ||
          (_this.promoCodeApplied && _this.subTotal < _this.stripeMin) ||
          (_this.discount && _this.subTotal < _this.stripeMin); // balance applied and subtotal is less than Stripe's minimum charge amount
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    _this.validation.module.component = this;
    // logging reaching payment step
    setTimeout(() => {
      try {
        Sentry.addBreadcrumb({
          message: "Threshold: payment.",
          data: {
            selectedRateSpeed: JSON.stringify(_this.selectedRateSpeed),
            subtotal: _this.subTotal,
            fees: _this.fees,
          },
          category: "payment",
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    }, 250);
    if (typeof window !== "undefined" && window.dataLayer) {
      const route = this.$route.name;
      let event = null;
      if (
        route === "events-event" ||
        route === "event" ||
        route === "partners-event"
      ) {
        event = this.currentEvent?.name
          ? this.currentEvent.name
          : this.$route.params.event;
      }
      try {
        window.dataLayer.push({
          event: "page_impression",
          pagePath: this.$route.path,
          pageTitle: event
            ? "Ship - " + event + " - Payment"
            : "Ship - Payment",
          visitorType: this.$auth.loggedIn ? "customer" : "guest",
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setSecondaryLoading: "setSecondaryLoading",
      setBoxes: "ship/setBoxes",
      setTrips: "ship/setTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",

      setPaymentDetails: "payment/setPaymentDetails",
      setBuckForBikes: "payment/setBuckForBikes",
      setOrderDetails: "payment/setOrderDetails",
      setPO: "payment/setPO",
      unsetPromoDiscount: "payment/unsetPromoDiscount",
      unsetDiscount: "payment/unsetDiscount",
      setCustomerBalanceApplied: "payment/setCustomerBalanceApplied",
      setPaymentMethod: "payment/setPaymentMethod",
      setPaymentProcessor: "payment/setPaymentProcessor",
      setSelectSavePaymentMethod: "payment/setSelectSavePaymentMethod",
    }),
    ...mapActions({
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      setStep: "ship/setStep",
      logRequest: "ship/logRequest",

      createOrder: "payment/createOrder",
      confirmOrder: "payment/confirmOrder",
      savePaidOrder: "payment/savePaidOrder",
      calculateSubTotal: "payment/calculateSubTotal",
      requestClientSecret: "payment/requestClientSecret",
      requestStripeUpdate: "payment/requestStripeUpdate",
      validatePromotion: "payment/validatePromotion",
      unmountPromoDiscount: "payment/unmountPromoDiscount",
      getPromoDiscount: "payment/getPromoDiscount",
      getDiscount: "payment/getDiscount",
      getPaymentMethods: "payment/getPaymentMethods",
      getUserPaypalToken: "user/getUserPaypalToken",
    }),
    // paypal
    async mountPaypalConfig(){
      let total = 0;
      if (!this.subtotalIsCovered) {
        total = parseFloat(
          Number(
            this.subTotal - (this.buckForBikes as number) <
              this.stripeMin
              ? this.subTotal
              : this.subTotal - (this.buckForBikes as number)
          ).toFixed(2)
        ) as number;
      }
      this.paypalAmount = total.toString();
      const token = await this.getUserPaypalToken();
      await this.$paypal.reload(token);
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (this.$paypal && typeof this.$paypal.Buttons === 'function' && total > 0 && !this.autoBillable) {
        this.$paypal.Buttons({
          style: {
            layout: 'vertical',
            color: 'blue',
            shape: 'rect',
            label: 'paypal'
          },
          onClick: async () => {
            // Aquí puedes validar antes de continuar
            await Promise.resolve(true);
          },
          createOrder: async (data: any, actions: any) => {            
            try {
              if (!actions || !actions.order) {
                throw new Error('Actions or actions.order is undefined');
              }
              // create-order runs first so PayPal gets the final order id as invoice_id.
              // create-order ignores the nonce for PayPal; confirm-order gets the PayPal id instead.
              const orderData = {
                nonce: "",
                storeCode: this.temporalOrderIds.id,
                purchaseOrder: this.paymentDetails.PO ? this.paymentDetails.PO : "",
                savePaymentMethod: true,
                donation: {
                  name: this.paymentDetails.buk_up_for_bikes?.id,
                  amount: this.paymentDetails.buk_up_for_bikes?.value,
                },
                total: this.paypalAmount as unknown as number,
                paymentProcessor : "P"
              };
              this.orderCreatedResponse = undefined;
              const createdOrder = await this.createOrder(orderData);
              if (!createdOrder?.id) {
                throw new Error('create-order failed before opening PayPal');
              }
              this.orderCreatedResponse = createdOrder;

              const orderId = String(createdOrder.id);
              const requestBodyObject = {
                total: this.paypalAmount,
                currency: 'USD',
                orderId,
                paymentIntentDescription: `Bikeflights.com: Shipping Charges Order# ${orderId}`,
                paymentIntentSoftDescriptor: '',
              }
              const cpiResponse = await this.$axios.$post(`/api/Paypal/cpi`, requestBodyObject);
              if (cpiResponse) {
                this.setPaymentProcessor("P");
                return cpiResponse.paypalOrderID;
              }
              return false
            } catch (error:any) {
              if (error.message?.includes('token')) {
                const newToken = await this.getUserPaypalToken();
                await this.$paypal.reload(newToken);
                await this.mountPaypalConfig();
              }
              throw error;
            }
          },
          onApprove: async(data:any, actions:any) => {
            if (!this.orderCreatedResponse) {
              return;
            }
            // Once we reach onApprove PayPal has authorized the payment; the capture
            // below charges the money. Remember the PayPal order id so that if anything
            // interrupts the in-memory confirmation chain after the charge (lost network
            // on mobile, tab switch, request timeout, thrown await) we can still recover
            // by re-confirming through the BE, which re-verifies the capture against PayPal.
            this.paypalCapturedOrderId = data.orderID;
            try {
              // call capture mode 2 -> charges the money
              const captureResponse = await this.$axios.$post(`/api/Paypal/capture-order`, {
                id: data.orderID,
                mode: "2",
              });
              if (captureResponse) {
                this.handlePaymentAuthorized(captureResponse);
                return captureResponse.id;
              }
              // Capture request resolved but returned nothing usable. The money may have
              // been charged already, so recover via the id-based fallback instead of
              // silently leaving the order unconfirmed.
              await this.attemptPaypalConfirmFallback(data.orderID);
            } catch (e) {
              // The capture request threw (e.g. the response was lost after the charge).
              // Recover through the BE, which will only mark the order paid if PayPal
              // reports the capture as COMPLETED.
              Sentry.captureException(e);
              await this.attemptPaypalConfirmFallback(data.orderID);
            }
          },
          onError: async (err: any) => {
            if (err.message?.includes('token') || err.message?.includes('auth')) {
              const newToken = await this.getUserPaypalToken();
              await this.$paypal.reload(newToken);
              await this.mountPaypalConfig(); // Reinicializar botones
            } else {
              this.handlePaymentError(err);
            }
          },
          fundingSource: 'paypal'
        }).render('#paypal-button-container');
      }
    },
    updatePaypalConfig(){
      const paypalButtonContainer = document.getElementById('paypal-button-container');
      let total = 0;
      if (!this.subtotalIsCovered) {
        total = parseFloat(
          Number(
            this.subTotal - (this.buckForBikes as number) <
              this.stripeMin
              ? this.subTotal
              : this.subTotal - (this.buckForBikes as number)
          ).toFixed(2)
        ) as number;
      }
      if (paypalButtonContainer && total !== 0) {
        paypalButtonContainer.innerHTML = ''; // clean the paypal button container
      }
      this.paypalAmount = '0';
      this.mountPaypalConfig();
    },
    // Stripe
    async mountStripeElement(): Promise<boolean> {
      const _this = this as any;
      try {
        if (
          (_this.subtotalIsCovered || _this.autoBillable) &&
          _this.clientToken
        ) {
          return true;
        }
        // start
        if (!_this.stripe) {
          // load stripe handler if it hasn't already.
          _this.stripe = await loadStripe(_this.$config.stripePK);
        }
        if (_this.stripe && _this.stripe.elements) {
          // init stripe elements if stripe loaded.
          const appearance = _this.paymentElementAppearance;
          let stripeTotal = 0;
          if (!_this.subtotalIsCovered) {
            stripeTotal = parseFloat(
              Number(
                _this.subTotal - (_this.buckForBikes as number) <
                  _this.stripeMin
                  ? _this.subTotal
                  : _this.subTotal - (_this.buckForBikes as number)
              ).toFixed(2)
            ) as number;
          }
          const savePaymentMethod = _this.isSavablePaymentMethod()
            ? _this.savePM
            : false;
          const clientSecretResponse = await _this.requestClientSecret({
            total: stripeTotal,
            currency: "usd",
            savePaymentMethod: savePaymentMethod,
            storecode: _this.temporalOrderIds?.id,
          });
          if (clientSecretResponse) {
            _this.clientToken = clientSecretResponse?.token;
            _this.autoBillable = clientSecretResponse.autoBillable;
            if (_this.autoBillable) {
              return true;
            }
            _this.elements = await _this.stripe.elements({
              clientSecret: clientSecretResponse?.token,
              loader: "always",
              appearance,
            });
          } else {
            _this.autoBillable = false;
            return false;
          }
        }
        _this.paymentElement = await _this.elements.create(
          "payment",
          _this.paymentElementOptions
        );
        if (document.getElementById("payment-element")) {
          // if the template contains the payment-element
          _this.paymentElement.mount("#payment-element");
          _this.stripeFrame = _this.paymentElement._frame;
          if (_this.paymentElement && _this.stripeFrame) {
            // if the element is there
            _this.paymentElement.on("change", _this.paymentElementEventHandler);
          } else {
            return false;
          }
          return true; // mounting completed
        }
      } catch (e) {
        Sentry.captureException(e);
        return false;
      }
      // in general, mountStripeElement will always return true unless there was an unhandled event
      return false;
    },
    async updateStripeElement() {
      const _this = this as any;
      if (!_this.autoBillable) {
        try {
          _this.setStripeLoading(true);
          // unmount or destroy the current payment element
          const wasStripeValid = _this.isStripeValid;
          _this.isStripeValid = false; // stripe is assumed invalid, so the button is locked
          _this.stripeElementMounted = true; // stripe is assumed mounted, so it needs to be unloaded
          let updateFailed = false;
          if (_this.clientToken) {
            let stripeTotal = 0;
            if (!_this.subtotalIsCovered) {
              stripeTotal = parseFloat(
                Number(
                  _this.subTotal - (_this.buckForBikes as number) <
                    _this.stripeMin
                    ? (_this.buckForBikes as number)
                    : _this.subTotal - (_this.buckForBikes as number) // since update doesn't include buckForBikes, we need to add it back if it's less than 1
                ).toFixed(2)
              ) as number;
            }
            const savePaymentMethod = _this.isSavablePaymentMethod()
              ? _this.savePM
              : false;
            if (stripeTotal) {
              const data = {
                total: stripeTotal,
                currency: "usd",
                savePaymentMethod: savePaymentMethod,
                paymentIntentId: _this.clientToken.split("_secret")[0],
                storecode: _this.temporalOrderIds?.id,
              };
              _this.setPaymentMethod(_this.clientToken.split("_secret")[0]);
              const stripeUpdateResponse = await _this.requestStripeUpdate(
                data
              );
              if (
                stripeUpdateResponse?.succeed &&
                stripeUpdateResponse?.amount / 100 === stripeTotal &&
                stripeUpdateResponse?.clientSecret === _this.clientToken
              ) {
                await _this.elements.fetchUpdates().then(function (result) {
                  if (!result || result.error) {
                    updateFailed = true;
                  }
                });
              } else {
                updateFailed = true;
              }
            }
          } else {
            _this.isStripeValid = false; // false if stripe was unloaded
            _this.stripeElementMounted = await _this.mountStripeElement(); // try mounting a new element
          }
          if (updateFailed) {
            if (_this.destroyStripeElement()) {
              _this.isStripeValid = false; // false if stripe was unloaded
              _this.stripeElementMounted = await _this.mountStripeElement(); // try mounting a new element
            } else {
              _this.stripeElementMounted = false; // stripe is no longer assumed to be mounted if destroy failed
              _this.stripeElementMounted = await _this.mountStripeElement(); // try again mounting a new element
            }
          }
          // stripeElementMounted would be replaced or added to a response from an event listener to the 'ready' event
          _this.isStripeValid = wasStripeValid && !!_this.stripeElementMounted;
          // if no errors, continue to hide loading gear
        } catch (e) {
          Sentry.captureException(e);
          _this.isStripeValid = false; // it is safe to assume that if something failed here, stripe is not valid
          _this.setStripeLoading(false);
        }
        _this.setStripeLoading(false);
      }
    },

    // Stripe// submit event handler
    async handleSubmit() {
      const _this = this as any;
      try {
        _this.clearActiveNotices();
        if (_this.selectedPaymentMethod && !_this.subtotalIsCovered) {
          // request client secret if payment method is selected and needed (subtotal is not covered)
          _this.savePM = true;
          let stripeTotal = 0;
          if (!_this.subtotalIsCovered) {
            stripeTotal = parseFloat(
              Number(
                _this.subTotal - (_this.buckForBikes as number) <
                  _this.stripeMin
                  ? _this.subTotal
                  : _this.subTotal - (_this.buckForBikes as number)
              ).toFixed(2)
            ) as number;
          }
          const savePaymentMethod = _this.isSavablePaymentMethod()
            ? _this.savePM
            : false;
          const clientSecretResponse = await _this.requestClientSecret({
            total: stripeTotal,
            currency: "usd",
            savePaymentMethod: savePaymentMethod,
            paymentMethod: _this.selectedPaymentMethod.id,
            storecode: _this.temporalOrderIds?.id,
          });
          if (clientSecretResponse) {
            _this.clientToken = clientSecretResponse?.token;
          }
        }

        const confirmationURL = new URL(
          _this.$router.resolve("/confirmation").href,
          window.location.origin
        ).href;
        let stripeTotal = 0;
        if (!_this.subtotalIsCovered) {
          stripeTotal = parseFloat(Number(_this.subTotal).toFixed(2)) as number;
        }
        if (this.savePM) {
          this.setSelectSavePaymentMethod(true);
        }
        const savePaymentMethod = _this.isSavablePaymentMethod()
          ? _this.savePM
          : false;
        const data = {
          nonce: _this.subtotalIsCovered
            ? ""
            : _this.clientToken.split("_secret")[0],
          storeCode: _this.temporalOrderIds.id,
          purchaseOrder: _this.paymentDetails.PO ? _this.paymentDetails.PO : "",
          savePaymentMethod: savePaymentMethod, // needs change or remove. not sure if needed when used with stripe element
          donation: {
            name: _this.paymentDetails.buk_up_for_bikes?.id,
            amount: _this.paymentDetails.buk_up_for_bikes?.value,
          },
          total: stripeTotal as unknown as number,
          paymentProcessor : "S"
        };
        _this.setPaymentProcessor("S");
        _this.setPaymentMethod(_this.clientToken.split("_secret")[0]);
        _this.orderCreatedResponse = false;
        _this.orderCreatedResponse = await _this.createOrder(data);

        if (
          (_this.selectedPaymentMethod && _this.orderCreatedResponse) ||
          (_this.subtotalIsCovered && _this.orderCreatedResponse)
        ) {
          try {
            // here, call the paymentIntent update to add description.
            if (!_this.subtotalIsCovered) {
              const description = _this.stripeDescription
                ?.replace("{orderID}", _this.orderCreatedResponse?.id)
                ?.replace("{subTotal}", String(stripeTotal));
              const stripeUpdateResponse = await _this.requestStripeUpdate({
                paymentIntentId: _this.clientToken.split("_secret")[0],
                PaymentIntentDescription: description,
                storecode: _this.temporalOrderIds?.id,
              });
              _this.setPaymentMethod(_this.clientToken.split("_secret")[0]);
            }
          } catch (e) {
            Sentry.captureException(e);
          }

          // request order confirmation if payment method is selected, order was created, or subtotal is covered order was created
          const data = {
            nonce: _this.subtotalIsCovered
              ? ""
              : _this.clientToken.split("_secret")[0],
            savePaymentMethod: savePaymentMethod,
            storeCode: _this.temporalOrderIds.id,
            OrderId: _this.orderCreatedResponse?.id,
            donation: {
              name: _this.paymentDetails.buk_up_for_bikes?.id,
              amount: _this.paymentDetails.buk_up_for_bikes?.value,
            },
            usedBalance: _this.customerBalanceApplied
              ? parseFloat(
                  Number(_this.customerBalanceApplied * 100).toFixed(0)
                )
              : (0 as number),
          };
          _this.setPaymentProcessor("S");
          _this.setPaymentMethod(_this.subtotalIsCovered ? "" : _this.clientToken.split("_secret")[0]);
          const response = await _this.confirmOrder(data);
          if (response && response.orderResponse) {
            // payment completed, call savePaidOrder
            _this.showMessage("Your payment was confirmed", 1);
            _this.validation.module.requestAlert({
              event: "valid",
              type: "order",
            });
            _this.setPaymentDetails(_this.paymentDetails);
            _this.setOrderDetails(response.orderResponse);
            _this.$router.push("/confirmation"); // needed, because the confirmation method is set to is_required.
          } else {
            // if here, then order confirmation failed with a known payment method and might have been charged. Really weird stuff.
            _this.validation.module.requestAlert({
              // need to change/create new alerts, for now we will be using the old ones
              event: {
                selector: "confirmPaymentFailed",
                type: _this.prettifyPaymentMethodName(
                  _this.selectedPaymentMethodType
                ), // need to rework this one, we need a way to extract the selected payment method and the label for it
              },
              type: "payment",
            });
            Sentry.captureEvent({
              message:
                "Your payment might have gone through but we were unable to confirm your order #" +
                _this.orderCreatedResponse?.id +
                ". Please contact support immediately to get help",
              level: "error", // or 'error', 'warning', 'debug', etc.
              // Business-critical: payment may have succeeded but order was not
              // confirmed. Tagged so beforeSend keeps it as a Sentry event.
              tags: { critical: "payment_unconfirmed" },
            });
            _this.setLoading(false);
          }
        } else if (_this.orderCreatedResponse) {
          try {
            // here, call the paymentIntent update to add description.
            if (!_this.subtotalIsCovered) {
              const description = _this.stripeDescription
                ?.replace("{orderID}", _this.orderCreatedResponse?.id)
                ?.replace("{subTotal}", String(stripeTotal));
              const stripeUpdateResponse = await _this.requestStripeUpdate({
                paymentIntentId: _this.clientToken.split("_secret")[0],
                PaymentIntentDescription: description,
                storecode: _this.temporalOrderIds?.id,
              });
              _this.setPaymentMethod(_this.subtotalIsCovered ? "" : _this.clientToken.split("_secret")[0]);
            }
          } catch (e) {
            Sentry.captureException(e);
          }

          // this is the regular order creation flow
          const confirmationURL = new URL(
            _this.$router.resolve("/pre-confirmation").href,
            window.location.origin
          );
          confirmationURL.searchParams.append(
            "orderId",
            _this.orderCreatedResponse.id
          );
          confirmationURL.searchParams.append(
            "storeCode",
            _this.temporalOrderIds.id
          );
          confirmationURL.searchParams.append(
            "usedBalance",
            (_this.customerBalanceApplied
              ? parseFloat(
                  Number(_this.customerBalanceApplied * 100).toFixed(0)
                )
              : 0) as unknown as string
          );
          confirmationURL.searchParams.append(
            "donationName",
            _this.paymentDetails?.buk_up_for_bikes
              ? _this.paymentDetails?.buk_up_for_bikes.id
              : ""
          );
          confirmationURL.searchParams.append(
            "donationAmount",
            _this.paymentDetails?.buk_up_for_bikes
              ? _this.paymentDetails?.buk_up_for_bikes.value
              : ""
          );
          confirmationURL.searchParams.append(
            "paymentMethodType",
            _this.selectedPaymentMethodType as string
          );
          const user = _this.session?.getUser();
          const confirmation = await _this.stripe
            .confirmPayment({
              elements: _this.elements,
              confirmParams: {
                return_url: confirmationURL?.href,
                payment_method_data: {
                  billing_details: {
                    name: user ? user.firstName + " " + user.lastName : "",
                    email: user ? user.email : "",
                    phone: user ? user.phoneNumber : "",
                  },
                },
              },
              redirect: "if_required",
            })
            .then(async function (result) {
              if (!result || result.error) {
                // This point will only be reached if there is an immediate error
                _this.isStripeValid = false;
                _this.isSubmitting = false;
                if (
                  result.error.type === "card_error" ||
                  result.error.type === "validation_error"
                ) {
                  // This is a temporal thing. This type of messages are out of the validation framework and need to be reconsidered, integrated or discarded
                  _this.showMessage(result.error.message);
                } else {
                  // This is a temporal thing. This type of messages are out of the validation framework and need to be reconsidered, integrated or discarded
                  _this.showMessage("An unexpected error occurred.");
                }
              } else {
                // payment completed, call confirmOrder
                const paymentIntent = result.paymentIntent;
                if (
                  paymentIntent.status === "requires_action" &&
                  _this.selectedPaymentMethodType === "cashapp"
                ) {
                  // here, status could also be "requires_payment_method" for a failed state with cashapp. However, the confirmation endpoint is handling that state and the flow continues if a different method is selected
                  _this.isStripeValid = false;
                  _this.isSubmitting = false;
                  _this.showMessage(
                    "Your payment was not confirmed, please try again."
                  );
                } else {
                  const savePaymentMethod = _this.isSavablePaymentMethod()
                    ? _this.savePM
                    : false;
                  _this.showMessage("Your payment was confirmed", 1);
                  _this.source = paymentIntent?.id;
                  _this.setPaymentMethod(paymentIntent);
                  const data = {
                    nonce: _this.source,
                    savePaymentMethod: savePaymentMethod,
                    storeCode: _this.temporalOrderIds.id,
                    OrderId: _this.orderCreatedResponse?.id,
                    donation: {
                      name: _this.paymentDetails.buk_up_for_bikes?.id,
                      amount: _this.paymentDetails.buk_up_for_bikes?.value,
                    },
                    usedBalance: _this.customerBalanceApplied
                      ? parseFloat(
                          Number(_this.customerBalanceApplied * 100).toFixed(0)
                        )
                      : (0 as number),
                    paymentProcessor: "S",
                  };
                  _this.setPaymentProcessor("S");
                  _this.setPaymentMethod(_this.source);
                  const response = await _this.confirmOrder(data);
                  if (response && response.orderResponse) {
                    _this.validation.module.requestAlert({
                      event: "valid",
                      type: "order",
                    });
                    _this.setPaymentDetails(_this.paymentDetails);
                    _this.setOrderDetails(response.orderResponse);
                    _this.$router.push("/confirmation"); // needed, because the confirmation method is set to is_required.
                  } else {
                    // if here, then order placement failed but payment was charged. Really sad stuff.
                    _this.validation.module.requestAlert({
                      // need to change/create new alerts, for now we will be using the old ones
                      event: {
                        selector: "confirmOrderFailed",
                        type: _this.prettifyPaymentMethodName(
                          _this.selectedPaymentMethodType
                        ), // need to rework this one, we need a way to extract the selected payment method and the label for it
                      },
                      type: "payment",
                    });
                    Sentry.captureEvent({
                      message:
                        "Your payment might have gone through but we were unable to confirm your order #" +
                        _this.orderCreatedResponse?.id +
                        ". Please contact support immediately to get help",
                      level: "error", // or 'error', 'warning', 'debug', etc.
                      // Business-critical: payment may have succeeded but order was not
                      // confirmed. Tagged so beforeSend keeps it as a Sentry event.
                      tags: { critical: "payment_unconfirmed" },
                    });
                    _this.setLoading(false);
                  }
                }
              }
            });
          return confirmation;
        } else {
          // if here, then order confirmation failed.
          _this.validation.module.requestAlert({
            // need to change/create new alerts, for now we will be using the old ones
            event: {
              selector: "failed",
              type: _this.prettifyPaymentMethodName(
                _this.selectedPaymentMethodType
              ), // need to rework this one, we need a way to extract the selected payment method and the label for it
            },
            type: "payment",
          });
          _this.setLoading(false);
        }

        return false;
      } catch (e) {
        Sentry.captureException(e);
        await _this.logRequest({
          value: { step: "Payment", success: false, data: [e] },
          agent: window ? window.navigator.userAgent : "",
        });
        _this.isSubmitting = false;
        return false;
      }
    },

    // Stripe// helpers
    showMessage(messageText, type = 0) {
      const messageContainer = document.querySelector(
        "#payment-message"
      ) as HTMLElement;
      const messageClass = type < 1 ? "danger" : type < 2 ? "success" : "info";
      messageContainer.classList.remove("hidden");
      messageContainer.classList.add(messageClass);
      messageContainer.textContent = messageText;

      setTimeout(function () {
        messageContainer.classList.add("hidden");
        messageContainer.classList.remove(messageClass);
        messageContainer.textContent = "";
      }, 4000);
    },
    destroyStripeElement() {
      try {
        const _this = this as any;
        if (_this.paymentElement) {
          // IF the alert for stripeBlocked must reactivate with each reload of the payment element, then add here _this.stripeFrame = undefined;
          _this.paymentElement.removeEventListener("change");
          _this.paymentElement.unmount();
        }
        _this.stripeElementMounted = false;
        return true;
      } catch (e) {
        return false;
      }
    },
    paymentElementEventHandler(event) {
      const _this = this as any;
      _this.stripeEvent = event;
      _this.selectedPaymentMethodType = event?.value?.type;
      _this.paymentDetails.selectedPaymentMethodType =
        _this.selectedPaymentMethodType;
      _this.paymentDetails.selectedPaymentMethod = undefined;
      if (_this.paymentDetails.selectedPaymentMethodType === "us_bank_account") {
        _this.validation.module.requestAlert({
          event: {
            selector: "ACHException",
          },
          type: "payment",
        });
      } else {
        _this.removeNotice("ACHExpectations");
      }
      if (_this.stripeEvent.empty || !_this.stripeEvent.complete) {
        _this.isStripeValid =
          (_this.selectedPaymentMethodType === "cashapp" ||
            _this.selectedPaymentMethodType === "google_pay" ||
            _this.selectedPaymentMethodType === "apple_pay") &&
          _this.stripeEvent.complete;
      } else {
        _this.isStripeValid = true;
      }
    },
    async fetchPaymentMethods() {
      (this as any).paymentMethods = await (this as any).getPaymentMethods();
    },
    selectPaymentMethod(method, PMIndex: number) {
      const _this = this as any;
      if ((this as any).selectedPaymentMethodIndex === PMIndex) {
        // already selected
        _this.selectedPaymentMethodIndex = 0;
        _this.selectedPaymentMethod = undefined;
        _this.paymentDetails.selectedPaymentMethod =
          _this.selectedPaymentMethod;
        _this.paymentDetails.selectedPaymentMethodType = _this.paymentDetails
          .selectedPaymentMethod?.type as string;
        _this.selectedPaymentMethodType =
          _this.paymentDetails.selectedPaymentMethodType;
      } else {
        // must select
        _this.selectedPaymentMethodIndex = PMIndex;
        _this.selectedPaymentMethod = method;
        _this.paymentDetails.selectedPaymentMethod =
          _this.selectedPaymentMethod;
        _this.paymentDetails.selectedPaymentMethodType = _this.paymentDetails
          .selectedPaymentMethod?.type as string;
        _this.selectedPaymentMethodType =
          _this.paymentDetails.selectedPaymentMethodType;
        if (
          _this.paymentDetails.selectedPaymentMethodType === "us_bank_account"
        ) {
          _this.validation.module.requestAlert({
            event: {
              selector: "ACHException",
            },
            type: "payment",
          });
        } else {
          _this.removeNotice("ACHExpectations");
        }
      }
    },
    useMethods() {
      (this as any).using = "methods";
      (this as any).destroyStripeElement();
      (this as any).validation.module.resetValidations();
    },
    async usePE() {
      const _this = this as any;
      _this.using = "element";
      _this.setStripeLoading(true);
      _this.stripeElementMounted = await _this.mountStripeElement(); // stripeElementMounted would be replaced or added to a response from an event listener to the 'ready' event
      _this.setStripeLoading(false);
      if (_this.selectedPaymentMethodIndex) {
        // payment already selected, reset it to prevent payment method overlapping.
        _this.selectedPaymentMethodIndex = 0;
        _this.selectedPaymentMethod = undefined;
        _this.paymentDetails.selectedPaymentMethod =
          _this.selectedPaymentMethod;
        _this.paymentDetails.selectedPaymentMethodType = _this.paymentDetails
          .selectedPaymentMethod?.type as string;
        _this.selectedPaymentMethodType =
          _this.paymentDetails.selectedPaymentMethodType;
      }
      (this as any).validation.module.resetValidations();
    },
    setStripeLoading(loading) {
      (this as any).stripeLoading = loading;
    },

    // promo discount
    async validatePromoCodeInput() {
      const _this = this as any;
      _this.removeNotice("AlertPromo");
      _this.removeNotice("AlertPromoAndDiscountFail");
      _this.removeNotice("AlertMinimumPromoAmount");
      _this.promoCodeValidated = true;
      // call validate promo code
      // validate promo code will be called when the coupon input changes. it can either call an endpoint to check the discount first, without applying, or just do a string check
      // to avoid overusing the endpoint, let's suppose it is just a basic string check
      // but if otherwise, then change the trigger on the input from v-on:input to @blur or @change, to avoid calling the endpoint on each alteration of the input
      _this.promoCodeIsValid = _this.promoCode.length > 3;
    },
    async removePromo() {
      const _this = this as any;
      _this.removeNotice("AlertPromo");
      _this.removeNotice("AlertPromoAndDiscountFail");
      _this.removeNotice("AlertMinimumPromoAmount");
      _this.unsetPromoDiscount();
      _this.promoCode = "";
      _this.promoCodeValidated = false;
      _this.promoCodeIsValid = false;
      _this.promoCodeApplied = false;
      _this.calculateSubTotal();
      // this is here so that if the promo code is removed, then the client token is reset, so that the customer can add a new card or bank or whatever
      _this.clientToken = _this.subtotalIsCovered ? "" : _this.clientToken;
      _this.setPaymentMethod(_this.subtotalIsCovered ? "" : _this.clientToken.split("_secret")[0]);
      _this.subtotalIsCovered =
        (_this.customerBalanceIsApplied && _this.subTotal < _this.stripeMin) ||
        (_this.promoCodeApplied && _this.subTotal < _this.stripeMin) ||
        (_this.discount && _this.subTotal < _this.stripeMin); // balance applied and subtotal is less than Stripe's minimum charge amount
      _this.adjustCustomerBalanceToApply();
      if (_this.using === "element") {
        await _this.updateStripeElement(); // recall the stripe initiation/refresh
      }
      this.updatePaypalConfig();
    },
    async applyPromo() {
      const _this = this as any;
      const promoDiscountResult = await _this.validatePromotion({
        couponCode: _this.promoCode,
        orderTotal: parseFloat(Number(_this.subTotal * 100).toFixed(0)),
      }); // call to endpoint
      _this.removeNotice("AlertPromo");
      _this.removeNotice("AlertPromoAndDiscountFail");
      _this.removeNotice("AlertMinimumPromoAmount");
      if (promoDiscountResult && promoDiscountResult.valid) {
        _this.promoCodeValidated = true;
        _this.promoCodeIsValid = true;
        _this.promoCodeApplied = true;
        _this.calculateSubTotal();

        if (
          _this.customerBalanceIsApplied &&
          _this.subTotal - _this.buckForBikes < 1
        ) {
          // if before reevaluating the balance, the subtotal was covered by it, then...
          _this.adjustCustomerBalance(); // this is here so that applying a promo after the balance is already in place and it covers the subtotal, then adding a promo will cause overcharging of balance
        } else {
          // it is entering here
          _this.adjustCustomerBalanceToApply();
        }

        _this.subtotalIsCovered =
          (_this.customerBalanceIsApplied &&
            _this.subTotal < _this.stripeMin) ||
          (_this.promoCodeApplied && _this.subTotal < _this.stripeMin) ||
          (_this.discount && _this.subTotal < _this.stripeMin); // balance applied and subtotal is less than Stripe's minimum charge amount

        if (_this.using === "element") {
          await _this.updateStripeElement(); // recall the stripe initiation/refresh
        }
        this.updatePaypalConfig();
      } else {
        if (
          promoDiscountResult.errorCode === "EC03" &&
          promoDiscountResult.hasOwnProperty("amount")
        ) {
          // here, if the conditions for promo code with minimum amount are found, asks for alert relating to that condition
          _this.validation.module.requestAlert({
            event: {
              selector: "minimumAmount",
              replacements: [_this.promoCode, promoDiscountResult.amount / 100],
            },
            type: "discount",
          });
        } else if (promoDiscountResult.errorCode === "EC09") {
          // here, if the conditions for promo code with discount being the same are found, asks for alert relating to that condition?
          _this.validation.module.requestAlert({
            event: {
              selector: "promoAndDiscount",
            },
            type: "discount",
          });
        } else {
          _this.validation.module.requestAlert({
            event: {
              selector: "promo",
            },
            type: "discount",
          });
        }
        _this.promoCodeIsValid = false;
        _this.promoCodeApplied = false;
      }
    },

    // customer balance
    validateCustomerBalance() {
      const _this = this as any;
      _this.customerBalanceValid =
        (_this.customerBalanceToApply as number) &&
        (_this.customerBalanceToApply as number) <=
          _this.customerBalanceAvailable &&
        (_this.customerBalanceToApply as number) <=
          (parseFloat(
            Number(_this.subTotal - (_this.buckForBikes as number)).toFixed(2)
          ) as number);
    },
    async removeCustomerBalance() {
      const _this = this as any;
      _this.customerBalanceToApply = 0;
      _this.setCustomerBalanceApplied((this as any).customerBalanceToApply);
      _this.customerBalanceIsApplied = false;
      // this is here so that if the customer balance is removed, then the client token is reset, so that the customer can add a new card or bank or whatever
      _this.clientToken = _this.subtotalIsCovered ? "" : _this.clientToken;
      _this.setPaymentMethod(_this.subtotalIsCovered ? "" : _this.clientToken.split("_secret")[0]);
      _this.subtotalIsCovered = false;
      //
      _this.calculateSubTotal();
      _this.adjustCustomerBalanceToApply();
      if (_this.using === "element") {
        await _this.updateStripeElement(); // recall the stripe initiation/refresh
      }
      this.updatePaypalConfig();
    },
    async applyCustomerBalance() {
      const _this = this as any;
      if (_this.customerBalanceValid) {
        _this.setCustomerBalanceApplied(_this.customerBalanceToApply);
        _this.customerBalanceIsApplied = true;
        _this.adjustCustomerBalanceToApply();
        _this.calculateSubTotal();
        _this.subtotalIsCovered =
          (_this.customerBalanceIsApplied &&
            _this.subTotal < _this.stripeMin) ||
          (_this.promoCodeApplied && _this.subTotal < _this.stripeMin) ||
          (_this.discount && _this.subTotal < _this.stripeMin); // balance applied and subtotal is less than Stripe's minimum charge amount
        if (_this.using === "element") {
          await _this.updateStripeElement(); // recall the stripe initiation/refresh
        }
        this.updatePaypalConfig();
      }
    },
    adjustCustomerBalanceToApply() {
      const _this = this as any;
      if (_this.customerBalanceIsApplied && _this.customerBalanceApplied) {
        _this.customerBalanceToApply = parseFloat(
          Number(_this.customerBalanceApplied).toFixed(2)
        ) as unknown as number;
      } else if (_this.customerBalanceAvailable > _this.subTotal) {
        _this.customerBalanceToApply = parseFloat(
          Number(_this.subTotal - _this.buckForBikes).toFixed(2)
        ) as unknown as number;
      } else if (
          _this.customerBalanceAvailable >
          _this.subTotal - _this.buckForBikes
        ) {
          _this.customerBalanceToApply = parseFloat(
            Number(_this.subTotal - _this.buckForBikes).toFixed(2)
          ) as unknown as number;
        } else {
          _this.customerBalanceToApply = _this.customerBalance;
        }
      
      _this.validateCustomerBalance();
    },
    adjustCustomerBalance() {
      const _this = this as any;
      _this.removeCustomerBalance();
      _this.adjustCustomerBalanceToApply();
      _this.applyCustomerBalance();
      _this.validateCustomerBalance();
    },

    // helpers
    async updateBuckUpValue(object: any) {
      const _this = this as any;
      const oldVal = _this.paymentDetails.buk_up_for_bikes;
      _this.paymentDetails.buk_up_for_bikes = object;
      _this.setBuckForBikes(object.value);
      _this.calculateSubTotal();
      _this.subtotalIsCovered =
        (_this.customerBalanceIsApplied && _this.subTotal < _this.stripeMin) ||
        (_this.promoCodeApplied && _this.subTotal < _this.stripeMin) ||
        (_this.discount && _this.subTotal < _this.stripeMin); // balance applied and subtotal is less than Stripe's minimum charge amount
      _this.adjustCustomerBalanceToApply();
      // _this.buckUpOpen = !object.value;
      if (
        _this.using === "element" &&
        (!oldVal || oldVal.value !== object.value)
      ) {
        this.updatePaypalConfig();
        await _this.updateStripeElement(); // recall the stripe initiation/refresh
      }
    },
    async validateStep() {
      const _this = this as any;
      try {
        _this.isSubmitting = true;
        if (await _this.validation.Validate()) {
          _this.isStripeValid = false;
          _this.isSubmitting = true;
          if (_this.selectedPaymentMethodType === "paypal") {
            this.setSecondaryLoading(true);
          } else {
            _this.setLoading(true);
          }
          await _this.logRequest(window ? window.navigator.userAgent : "");
          if (_this.autoBillable) {
            // legacy for autoBilling
            if (_this.temporalOrderIds) {
              const data = {
                nonce: "",
                storeCode: _this.temporalOrderIds.id, // order id
                purchaseOrder: _this.paymentDetails.PO
                  ? _this.paymentDetails.PO
                  : "", // PO field
                savePaymentMethod: false, // needs change or remove. not sure if needed when used with stripe element
                donation: {
                  name: _this.paymentDetails.buk_up_for_bikes?.id,
                  amount: _this.paymentDetails.buk_up_for_bikes?.value,
                },
                total: _this.subTotal, // subtotal
              };
              _this.setPaymentProcessor("");
              _this.setPaymentMethod("");
              _this.orderCreatedResponse = false;
              _this.orderCreatedResponse = await _this.createOrder(data);
              if (_this.orderCreatedResponse) {
                const data = {
                  nonce: "",
                  savePaymentMethod: false,
                  storeCode: _this.temporalOrderIds.id,
                  OrderId: _this.orderCreatedResponse?.id,
                  donation: {
                    name: _this.paymentDetails.buk_up_for_bikes?.id,
                    amount: _this.paymentDetails.buk_up_for_bikes?.value,
                  },
                  usedBalance: _this.customerBalanceApplied
                    ? parseFloat(
                        Number(_this.customerBalanceApplied * 100).toFixed(0)
                      )
                    : (0 as number),
                };
                const response = await _this.confirmOrder(data);
                if (response && response.orderResponse) {
                  _this.validation.module.requestAlert({
                    event: "valid",
                    type: "order",
                  });
                  _this.setPaymentDetails(_this.paymentDetails);
                  _this.setOrderDetails(response.orderResponse);
                  _this.$router.push("/confirmation"); // needed, because the confirmation method is set to is_required.
                } else {
                  _this.validation.module.requestAlert({
                    event: { selector: "failed", type: "Internal Billing" },
                    type: "payment",
                  });
                  _this.setLoading(false);
                }
              } else {
                _this.validation.module.requestAlert({
                  event: { selector: "failed", type: "Internal Billing" },
                  type: "payment",
                });
                _this.setLoading(false);
              }
            }
          } else if (_this.selectedPaymentMethodType === "paypal") {
            await this.handlePaypalSubmit();
          } else {
            // this method should handle all related to payment confirmation and order creation
            await _this.handleSubmit();
          }
        } else {
          _this.isStripeValid = false;
          _this.isSubmitting = false;
          _this.setLoading(false);
          this.setSecondaryLoading(false);
        }
      } catch (e) {
        _this.isStripeValid = false;
        _this.isSubmitting = false;
        _this.setLoading(false);
        this.setSecondaryLoading(false);
      }
      _this.isSubmitting = false;
      _this.setLoading(false);
      this.setSecondaryLoading(false);
    },
    formatPrices(value: any) {
      return Number(parseFloat(value as string).toFixed(2)).toLocaleString(
        "en-US",
        { maximumFractionDigits: 2, minimumFractionDigits: 2 }
      );
    },
    prettifyPaymentMethodName(string: string) {
      if (string && string === "us_bank_account") {
        return "Bank Account";
      } else
        return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
    },
    openModalToS(value: boolean) {
      this.isOpenModalToS = value;
    },
    closeModalToS() {
      this.isOpenModalToS = false;
    },
    openModalSP(value: boolean) {
      this.isOpenModalSP = value;
    },
    closeModalSP() {
      this.isOpenModalSP = false;
    },
    isSavablePaymentMethod() {
      const _this = this as any;
      const logged = _this.session && _this.session.sessionType;
      const canSave = _this.stripeElementMounted && !_this.subtotalIsCovered;
      const elementReady =
        _this.using === "element" && _this.stripeElementMounted;
      const savableMethod =
        _this.selectedPaymentMethodType !== "klarna" &&
        _this.selectedPaymentMethodType !== "cashapp";
      return logged && canSave && elementReady && savableMethod;
    },
    async handlePaypalSubmit() {
      const _this = this as any;
      try {
        _this.clearActiveNotices();

        const confirmationURL = new URL(
          _this.$router.resolve("/confirmation").href,
          window.location.origin
        ).href;


        const savePaymentMethod = false;
        if (
          (_this.selectedPaymentMethod && _this.orderCreatedResponse) ||
          (_this.subtotalIsCovered && _this.orderCreatedResponse)
        ) {
          const data = {
            nonce: _this.subtotalIsCovered
              ? ""
              : this.paymentDetails.selectedPaymentMethod.id,
           savePaymentMethod,
            storeCode: _this.temporalOrderIds.id,
            OrderId: _this.orderCreatedResponse?.id,
            donation: {
              name: _this.paymentDetails.buk_up_for_bikes?.id,
              amount: _this.paymentDetails.buk_up_for_bikes?.value,
            },
            usedBalance: _this.customerBalanceApplied
              ? parseFloat(
                  Number(_this.customerBalanceApplied * 100).toFixed(0)
                )
              : (0 as number),
            paymentProcessor : "P"
          };
          _this.setPaymentProcessor("P");
          _this.setPaymentMethod(_this.subtotalIsCovered ? "" : this.paymentDetails.selectedPaymentMethod.id);
          const response = await _this.confirmOrder(data);
          if (response && response.orderResponse) {
            // payment completed, call savePaidOrder
            _this.showMessage("Your payment was confirmed", 1);
            _this.validation.module.requestAlert({
              event: "valid",
              type: "order",
            });
            _this.setPaymentDetails(_this.paymentDetails);
            _this.setOrderDetails(response.orderResponse);
            _this.$router.push("/confirmation"); // needed, because the confirmation method is set to is_required.
          } else {
            // if here, then order confirmation failed with a known payment method and might have been charged. Really weird stuff.
            _this.validation.module.requestAlert({
              // need to change/create new alerts, for now we will be using the old ones
              event: {
                selector: "confirmPaymentFailed",
                type: 'paypal',
              },
              type: "payment",
            });
            Sentry.captureEvent({
              message:
                "Your payment might have gone through but we were unable to confirm your order #" +
                _this.orderCreatedResponse?.id +
                ". Please contact support immediately to get help",
              level: "error", // or 'error', 'warning', 'debug', etc.
              // Business-critical: payment may have succeeded but order was not
              // confirmed. Tagged so beforeSend keeps it as a Sentry event.
              tags: { critical: "payment_unconfirmed" },
            });
            _this.setSecondaryLoading(false);
          }
        } else {
          // We got here without a usable selectedPaymentMethod. If a PayPal capture
          // already happened its money may be charged, so try the id-based recovery
          // before surfacing a failure to the user.
          const recovered = await _this.attemptPaypalConfirmFallback(_this.paypalCapturedOrderId);
          if (!recovered) {
            // if here, then order confirmation failed.
            _this.validation.module.requestAlert({
              // need to change/create new alerts, for now we will be using the old ones
              event: {
                selector: "failed",
                type: 'paypal', // need to rework this one, we need a way to extract the selected payment method and the label for it
              },
              type: "payment",
            });
            _this.setSecondaryLoading(false);
          }
        }

        return false;
      } catch (e) {
        Sentry.captureException(e);
        await _this.logRequest({
          value: { step: "Payment", success: false, data: [e] },
          agent: window ? window.navigator.userAgent : "",
        });
        _this.isSubmitting = false;
        return false;
      }
    },
    // Recovery path for the PayPal inline button: when the in-memory confirmation chain
    // breaks after the capture has charged the money, re-confirm the order using only the
    // PayPal order id. The BE re-verifies the capture against PayPal and only marks the
    // order paid if PayPal reports it COMPLETED, so this is safe and idempotent.
    // Returns true if the order ended up confirmed, false otherwise.
    async attemptPaypalConfirmFallback(paypalOrderId: string): Promise<boolean> {
      if (!paypalOrderId || !this.orderCreatedResponse) {
        return false;
      }
      try {
        const data = {
          nonce: paypalOrderId,
          savePaymentMethod: false,
          storeCode: this.temporalOrderIds.id,
          OrderId: (this.orderCreatedResponse as any)?.id,
          donation: {
            name: this.paymentDetails.buk_up_for_bikes?.id,
            amount: this.paymentDetails.buk_up_for_bikes?.value,
          },
          usedBalance: this.customerBalanceApplied
            ? parseFloat(Number(this.customerBalanceApplied * 100).toFixed(0))
            : 0,
          paymentProcessor: "P",
        };
        this.setPaymentProcessor("P");
        const response = await this.confirmOrder(data);
        if (response && response.orderResponse) {
          this.showMessage("Your payment was confirmed", 1);
          this.validation.module.requestAlert({ event: "valid", type: "order" });
          this.setPaymentDetails(this.paymentDetails);
          this.setOrderDetails(response.orderResponse);
          this.$router.push("/confirmation");
          return true;
        }
        // BE could not confirm (PayPal did not report the capture as completed).
        // Surface the business-critical event so support can reconcile.
        Sentry.captureEvent({
          message:
            "Your payment might have gone through but we were unable to confirm your order #" +
            (this.orderCreatedResponse as any)?.id +
            ". Please contact support immediately to get help",
          level: "error",
          tags: { critical: "payment_unconfirmed" },
        });
        return false;
      } catch (e) {
        Sentry.captureException(e);
        return false;
      }
    },
    handlePaymentAuthorized(data: any,) {
      this.selectedPaymentMethodIndex = 9;
      this.selectedPaymentMethod = {
        id: data.id,
        object: "payment_method",
        livemode: false,
        type: "paypal",
      };
      this.paymentDetails.selectedPaymentMethod =
        this.selectedPaymentMethod;
      this.paymentDetails.selectedPaymentMethodType = this.paymentDetails
        .selectedPaymentMethod?.type as string;
      this.selectedPaymentMethodType =
        this.paymentDetails.selectedPaymentMethodType;
      this.validateStep();
    },

    handlePaymentError(err: any) {
      this.validation.module.requestAlert({
        event: {
          selector: "failed",
          type: 'paypal',
        },
        type: "payment",
      });
      this.setLoading(false);
      Sentry.captureException(err);
    },
  },
  beforeDestroy() {
    (this as any).destroyStripeElement();
    (this as any).validation.module.resetValidations();
  },
});
